import React from "react";
import { useSavedWordPopover } from "../SavedWords/SavedWordsProvider";
import lessonTextStyles from "../LessonText/lessonText.module.scss";

// Blog posts have no curated reading dictionary (unlike LessonText's lesson
// prose), so a contiguous run of Japanese script is treated as one saveable
// unit with no known reading - the popover looks up reading/meaning itself.
const japaneseRun = /[\p{Script=Han}々\p{Script=Hiragana}\p{Script=Katakana}ー]+/gu;

// A lone single kana/kanji character (common in conjugation-pattern tables,
// e.g. the one moving kana in 売<strong>ら</strong>ない) has little save
// value on its own and clutters already-dense table cells and prose - only
// runs of 2 or more characters become clickable. Counted in code points
// (not UTF-16 units), so a single supplementary-plane kanji still counts as one.
function isLinkable(value: string): boolean {
    return Array.from(value).length >= 2;
}

type Segment = { linkable: boolean; value: string };

function splitJapaneseRuns(text: string): Segment[] {
    const segments: Segment[] = [];
    let cursor = 0;
    for (const match of text.matchAll(japaneseRun)) {
        const index = match.index ?? 0;
        if (index > cursor) segments.push({ linkable: false, value: text.slice(cursor, index) });
        segments.push({ linkable: isLinkable(match[0]), value: match[0] });
        cursor = index + match[0].length;
    }
    if (cursor < text.length) segments.push({ linkable: false, value: text.slice(cursor) });
    return segments;
}

/**
 * Makes every contiguous run of Japanese script (of 2+ characters) in plain
 * blog text clickable, opening the same saved-word popover used on the
 * Lessons pages. No reading is attached - the popover's own lookup resolves
 * it from the saved text.
 */
export default function JapaneseWordLink({ text }: { text: string }) {
    const open = useSavedWordPopover();
    if (!open) return <>{text}</>;

    const segments = splitJapaneseRuns(text);
    if (!segments.some((segment) => segment.linkable)) return <>{text}</>;

    return (
        <>
            {segments.map((segment, index) =>
                segment.linkable ? (
                    <button
                        key={index}
                        type="button"
                        className={lessonTextStyles.wordButton}
                        onClick={(event) => open(segment.value, undefined, event.currentTarget)}
                    >
                        {segment.value}
                    </button>
                ) : (
                    <React.Fragment key={index}>{segment.value}</React.Fragment>
                )
            )}
        </>
    );
}
